import type { UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import isDraftSignal from "app/pages/sales-intelligence/sub-modules/signals/models/core/type-guards/isDraftSignal";
import { signalsQueryKeys } from "app/pages/sales-intelligence/sub-modules/signals/query/key-factory";
import {
    createDraftSignalSuccess,
    createSignalAsync,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";

import type Signal from "../../models/core/Signal";
import type SignalFilters from "../../models/core/SignalFilters";
import type SignalCreateDTO from "../../models/miscellaneous/SignalCreateDTO";

export type CreateSignalResult = { created: boolean; signal: Signal };

type PartialUseMutationOptions<F extends SignalFilters> = Omit<
    Partial<UseMutationOptions<CreateSignalResult, Error, SignalCreateDTO<F>>>,
    "mutationFn" | "mutationKey"
>;

type Options<F extends SignalFilters> = PartialUseMutationOptions<F> & {
    keyIdentifier?: string;

    /**
     * Optional function to find existing duplicate signal.
     * If returns a signal, mutation succeeds with that signal instead of creating new one.
     */
    findDuplicate?: (draftSignal: SignalCreateDTO<F>) => Signal<F> | undefined;
};

export function useCreateSignalMutation<F extends SignalFilters>(
    options: Options<F> = {},
): UseMutationResult<CreateSignalResult, Error, SignalCreateDTO<F>> {
    const dispatch = useDispatch();

    const { keyIdentifier, findDuplicate, onSuccess, ...restOptions } = options;

    return useMutation<CreateSignalResult, Error, SignalCreateDTO<F>>({
        mutationKey: signalsQueryKeys.createSignal(keyIdentifier),
        retry: false,
        gcTime: Number.POSITIVE_INFINITY,

        mutationFn: async (dto) => {
            const existingSignal = findDuplicate?.(dto);

            if (existingSignal) {
                return { signal: existingSignal, created: false };
            }

            const [signal] = await salesApiService.signals.createSignals([dto]);

            return { signal, created: true };
        },

        onSuccess: (result, draftSignal, context) => {
            if (result.created) {
                if (isDraftSignal(result.signal)) {
                    dispatch(createDraftSignalSuccess(result.signal));
                } else {
                    dispatch(createSignalAsync.success(result.signal));
                }
            }

            onSuccess?.(result, draftSignal, context);
        },

        ...restOptions,
    });
}
